import * as React from 'react';

type Crumb = {
  label: string;
  onClick?: () => void;
};

type Props = {
  crumbs: Crumb[];
  editable?: boolean;              // if true, last crumb is an editable text field
  onRename?: (name: string) => void;
};

/**
 * V3Breadcrumb — workspace breadcrumb trail with optional inline rename.
 *
 * Layout: "Projects > {name}" with separator chevrons. Clicking
 * non-final crumbs navigates. The final crumb can be an editable
 * inline text field (pencil icon) when `editable` is true — used
 * for renaming the current project.
 *
 * Matches existing HeaderMiddleBar's CustomName pattern: controlled
 * locally, commits on blur via onRename callback.
 */
const V3Breadcrumb: React.SFC<Props> = ({
  crumbs,
  editable = false,
  onRename,
}) => (
  <div className="v3-breadcrumb">
    {crumbs.map((c, i) => {
      const isLast = i === crumbs.length - 1;
      return (
        <span key={i}>
          {i > 0 && <span className="v3-breadcrumb-sep">›</span>}
          {isLast && editable ? (
            <span className="v3-breadcrumb-editable">
              <input
                className="v3-breadcrumb-input"
                defaultValue={c.label}
                onBlur={(e) => onRename && onRename(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                }}
              />
              <svg className="v3-breadcrumb-pencil" width="12" height="12" viewBox="0 0 12 12" fill="none">
                <path d="M8.5 1.5l2 2-7 7H1.5V8.5z" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round"/>
              </svg>
            </span>
          ) : (
            <span
              className={`v3-breadcrumb-item ${c.onClick ? 'clickable' : ''}`}
              onClick={c.onClick}
            >
              {c.label}
            </span>
          )}
        </span>
      );
    })}
  </div>
);

export default V3Breadcrumb;
